import Infographic from '~/components/InfoPages/Infographic';
import styles from '~/components/InfoPages/InfoPage.module.scss';
import infographicStyles from '~/components/InfoPages/Infographic.module.scss';
import Head from '~/components/Meta/Head';
import generateArticleSchema from '~/utils/Schema/article';

import Hero from '@referralexchange/rea-ui-react/src/components/blocks/Hero';
import Image from '@referralexchange/rea-ui-react/src/atoms/Image';
import Anchors from '@referralexchange/rea-ui-react/src/components/navigation/Anchors';
import MessageAgentCta from '~/components/blocks/MessageAgentCta';
import SidebarContainer from '@referralexchange/rea-ui-react/src/components/blocks/SidebarContainer';
import Content from '@referralexchange/rea-ui-react/src/atoms/Content';
import Heading from '@referralexchange/rea-ui-react/src/atoms/Heading';
import Section from '@referralexchange/rea-ui-react/src/atoms/Section';

import { WORKING_WITH_AN_AGENT_FUNNEL } from '~/config/funnel';
import Link from 'next/link';
import { useState } from 'react';

const InfoPage = (props) => {
  const sidebar = (
    <Anchors
      items={[
        {
          name: (
            <MessageAgentCta
              standalone
              funnel={WORKING_WITH_AN_AGENT_FUNNEL}
              fullInteractive
            />
          ),
          href: null,
          hideOnMobile: true,
        },
        {
          name: 'Why Work with a Real Estate Agent?',
          href: '#why-work-with-a-real-estate-agent',
        },
        {
          name: 'Why Work with a REALTOR®?',
          href: '#why-work-with-a-realtor',
        },
        {
          name: 'How Can I Get Real Estate Agent and REALTOR® Recommendations?',
          href: '#how-can-i-get-real-estate-agent-and-realtor-recommendations',
        },
      ]}
    />
  );

  const articleSchema = generateArticleSchema({
    headline: 'Working with a Real Estate Agent',
    description:
      'Learn more about the specifics of why you should work with with a top real estate agent for your buying or selling home transaction. RealEstateAgents.com features the best agents in your area.',
    url: 'https://www.realestateagents.com/working-with-an-agent',
    slug: 'working-with-an-agent',
    imageUrl: 'https://www.realestateagents.com/static/working-with-an-agent/working-with-an-agent-hero.png',
    imageAlt: 'Work with a real estate agent',
    datePublished: '2023-10-18',
    dateModified: '2025-10-23',
    breadcrumbItems: [
      {
        name: 'Working with a Real Estate Agent',
        url: 'https://www.realestateagents.com/working-with-an-agent',
      },
    ],
  });

  return (
    <>
      <Hero
        heading="Working with a Real Estate Agent"
        content="Discover the importance of working with Real Estate Agents."
        image={
          <Image
            src={'/static/working-with-an-agent/working-with-an-agent-hero.png'}
            alt="Work with a real estate agent"
          />
        }
      />
      <Head
        url="https://www.realestateagents.com/working-with-an-agent"
        title={'Working with a Real Estate Agent | RealEstateAgents.com'}
        schema={articleSchema}
        description="Learn more about the specifics of why you should work with with a top real estate agent for your buying or selling home transaction. RealEstateAgents.com features the best agents in your area."
      />
      <SidebarContainer
        sidebar={sidebar}
        mobileMenuProps={{
          top: true,
          defaultTitle: 'Working with a Real Estate Agent',
        }}
        mobileMenuEnabled={true}
      >
        <Content>
          <Section>
            <Heading
              tag="h2"
              type="h1"
              anchor="Why Work with a Real Estate Agent?"
            >
              Why Work with a Real Estate Agent?
            </Heading>
            <p>
              Buying or selling a home is a major life event which creates both
              excitement and stress! The amount of information available and the
              number of services claiming to be a one-stop-shop can be
              overwhelming. The stakes are high with such a{' '}
              <Link
                className={styles['InfoPage__link']}
                target={'_blank'}
                href={'/blog/all/how-to-sell-your-home-for-its-highest-value'}
              >
                major financial decision
              </Link>
              , and to get the best outcome you'll want to find a top real
              estate agent who will be on YOUR side. Working with a great real
              estate agent will significantly enhance your experience and
              transaction.
            </p>
            <p>
              A good real estate agent acts on your behalf throughout your real
              estate journey. A real estate agent will help you in a number of
              ways and have access to resources unavailable to non-agents in
              your area, like listings of homes for sale, connections to
              partners within the industry (lenders, builders, and more), and
              understands the real estate transaction process, including jargon
              like “escrow”, “days on market”, and more.
            </p>
            <p>
              If you're a buyer, this means they can help with neighborhood
              identification,{' '}
              <Link
                className={styles['InfoPage__link']}
                target={'_blank'}
                href="/how-to-find-homes-for-sale-near-you"
              >
                home selection
              </Link>
              ,{' '}
              <Link
                className={styles['InfoPage__link']}
                target={'_blank'}
                href="/blog/buying/5-tips-to-make-your-offer-stand-out"
              >
                submitting an offer
              </Link>
              , negotiating, and more. For real estate sellers, an{' '}
              <Link
                className={styles['InfoPage__link']}
                target={'_blank'}
                href="/blog/all/5-reasons-its-important-to-price-your-home-right-from-the-start"
              >
                agent can support pricing
              </Link>
              , listing, fielding offers, and moving your home sale forward. A
              top real estate agent will support your purchase or sale from
              start to finish and make sure your real estate dreams come true.
              Although it’s hard to{' '}
              <Link
                className={styles['InfoPage__link']}
                target={'_blank'}
                href="/how-to-compare-real-estate-agents"
              >
                compare real estate agents
              </Link>{' '}
              for your transaction, in time you’ll be happy you took the time to
              screen agents and identify the best real estate agent.
            </p>
            <Infographic title={'Benefits of working with an agent'}>
              <div className={`${infographicStyles['Infographic__body']}`}>
                <div
                  className={infographicStyles['Infographic__benefits-item']}
                >
                  <div
                    className={
                      infographicStyles['Infographic__title__benefits']
                    }
                  >
                    For Buyers
                  </div>
                  <div
                    className={infographicStyles['Infographic__benefits-text']}
                  >
                    Neighborhood identification
                  </div>
                  <div
                    className={infographicStyles['Infographic__benefits-text']}
                  >
                    Home selection
                  </div>
                  <div
                    className={infographicStyles['Infographic__benefits-text']}
                  >
                    Submitting an offer
                  </div>
                  <div
                    className={infographicStyles['Infographic__benefits-text']}
                  >
                    Negotiating
                  </div>
                </div>
                <div
                  className={infographicStyles['Infographic__benefits-item']}
                >
                  <div
                    className={
                      infographicStyles['Infographic__title__benefits']
                    }
                  >
                    For Sellers
                  </div>
                  <div
                    className={infographicStyles['Infographic__benefits-text']}
                  >
                    Pricing support
                  </div>
                  <div
                    className={infographicStyles['Infographic__benefits-text']}
                  >
                    Listing support
                  </div>
                  <div
                    className={infographicStyles['Infographic__benefits-text']}
                  >
                    Fielding offers
                  </div>
                  <div
                    className={infographicStyles['Infographic__benefits-text']}
                  >
                    Moving home sale forward
                  </div>
                </div>
              </div>
            </Infographic>
            <p>
              Whether you're a first time buyer or you've done this several
              times before,{' '}
              <Link
                className={styles['InfoPage__link']}
                target={'_blank'}
                href="/how-to-compare-real-estate-agents"
              >
                comparing and choosing the best real estate agent
              </Link>{' '}
              can help guarantee that you get the most from the experience.
            </p>
            <p>
              According to the{' '}
              <Link
                className={styles['InfoPage__link']}
                target={'_blank'}
                href={
                  'https://www.nar.realtor/sites/default/files/documents/2022-home-buyers-and-sellers-generational-trends-03-23-2022.pdf'
                }
              >
                National Association of REALTORS®
              </Link>{' '}
              (NAR) the typical home buyer found several benefits provided by
              real estate agents including:{' '}
              <Link
                className={styles['InfoPage__link']}
                target={'_blank'}
                href="/blog/buying/the-homebuying-journey-6-steps-for-first-time-homebuyers"
              >
                Helping understand the process
              </Link>
              , pointed out unnoticed features and faults with potential
              properties, knowledge about quality service providers in the area
              (e.g. inspectors, contractors, financing, etc), better negotiated
              terms, shortening the process and improving the buyer's knowledge
              of search areas. These are just some of the benefits that choosing
              the{' '}
              <Link
                className={styles['InfoPage__link']}
                target={'_blank'}
                href="/how-a-real-estate-agent-works-for-you"
              >
                best local real estate agent will provide
              </Link>{' '}
              on the buyer side.
            </p>
            <p>
              Sellers also have a tremendous amount to gain from working with a
              top real estate agent. Oftentimes property owners might try to
              sell their property without a real estate agent. In 2021 around
              10% of all purchase transactions were done as{' '}
              <Link
                className={styles['InfoPage__link']}
                target={'_blank'}
                href={
                  'https://www.nar.realtor/sites/default/files/documents/2022-highlights-from-the-profile-of-home-buyers-and-sellers-report-11-03-2022_0.pdf'
                }
              >
                For-Sale-by-Owner
              </Link>{' '}
              (FSBO) and these properties were sold at a median price of
              $225,000, significantly lower than the median of agent assisted
              homes at{' '}
              <Link
                className={styles['InfoPage__link']}
                target={'_blank'}
                href={
                  'https://www.nar.realtor/sites/default/files/documents/2022-highlights-from-the-profile-of-home-buyers-and-sellers-report-11-03-2022_0.pdf'
                }
              >
                $345,000
              </Link>
              . That is an average of 35% more that you as a seller could be
              leaving on the table if you don't work with a top real estate
              agent.
            </p>
            <Infographic title={'Median purchase price of transactions'}>
              <div className={`${infographicStyles['Infographic__body']}`}>
                <div className={infographicStyles['Infographic__price-item']}>
                  <div className={infographicStyles['Infographic__number']}>
                    $225,000
                  </div>
                  <img
                    src={'/static/working-with-an-agent/townhouse-icon.svg'}
                    className={
                      infographicStyles['Infographic__price-item-icon']
                    }
                    alt="FSBO home sell for less"
                  />
                  <div>For-Sale-by-Owner (FSBO)</div>
                </div>
                <div
                  className={`${infographicStyles['Infographic__price-item']} ${infographicStyles['--highlight']}`}
                >
                  <div
                    className={`${infographicStyles['Infographic__number']} ${infographicStyles['--highlight']}`}
                  >
                    $345,000
                  </div>
                  <img
                    src={'/static/working-with-an-agent/townhouse-icon.svg'}
                    className={`${infographicStyles['Infographic__price-item-icon']} ${infographicStyles['--highlight']}`}
                    alt="Agent assisted sales sell for more"
                  />
                  <div>Agent Assisted Homes</div>
                </div>
              </div>
            </Infographic>
            <p>
              For example, let's imagine you're a home seller with a home worth
              about $300,000. If you attempted to sell the home yourself as a
              FSBO you lack the experience and connections to properly market
              the home, negotiate, and weed-out lowball offers, so you might end
              up losing the above noted 35% value, dropping your closing sale
              price to $195,000. If you were to work with an average or below
              average, you might close for $300,000, pay the agent a 3%
              commission of $9,000, and end up netting $291,000. However a top
              agent might be able to negotiate up or create a more competitive
              listing, upping the closing price by 10% to $330,000. After paying
              a 3% commission you're making $320,100; that's a difference of
              $29,100 in your pocket!
            </p>
            <p>
              In addition to all the above added benefits, using the best local
              real estate agent can save you from future financial risk if you
              don't know how to skillfully evaluate a property, improperly
              complete necessary transaction paperwork, or fail to do your due
              diligence when weighing financing options. A skilled, veteran real
              estate agent can guide your transactions with their years of
              experience and technical know how.
            </p>
            <p>
              There are near countless benefits that come with finding and
              working with the best real estate agent. Although it does take
              time to{' '}
              <Link
                className={styles['InfoPage__link']}
                target={'_blank'}
                href="/how-to-find-a-real-estate-agent"
              >
                find
              </Link>{' '}
              and{' '}
              <Link
                className={styles['InfoPage__link']}
                target={'_blank'}
                href="/how-to-compare-real-estate-agents"
              >
                compare real estate agents
              </Link>
              , you will ultimately save time compared to if you attempted to
              manage the entire transaction yourself. Plus, whether you're a
              first time or veteran home buyer or seller, you know the stakes
              are high when it comes to a real estate transaction. With markets
              becoming more competitive everyday, the right agent can ensure
              your transaction goes just the way you want it to, so it’s
              important to find a real estate agent near you who understands and
              can manage the real estate process.
            </p>
            <p>
              At the end of the day, your real estate agent and real estate
              broker should be advocates for making your transaction a success.
              The best real estate agents are your partners throughout the
              transaction, providing expertise, support, and a reassuring
              presence ensuring you achieve the outcomes you're aiming for.
            </p>
          </Section>
          <MessageAgentCta
            standalone
            funnel={WORKING_WITH_AN_AGENT_FUNNEL}
            fullInteractive
          />
          <Section>
            <Heading tag="h2" type="h1" anchor="Why Work with a REALTOR®?">
              Why Work with a REALTOR®?
            </Heading>
            <p>
              You may have heard the terms Real Estate Agent and REALTOR® used
              interchangeably - however they are not the same thing. While you
              become a real estate agent after you have have passed the state
              licensing exam and begin practicing at a brokerage, a REALTOR® is
              “a real estate professional who is a member of the National
              Association or REALTORS® and subscribes to its strict Code of
              Ethics”.
            </p>
            <div className={styles['InfoPage__callout']}>
              So although all REALTORS® are real estate agents, not all real
              estate agents are REALTORS®.
            </div>
            <p>
              Working with a REALTOR® can have a number of benefits over working
              with someone who is a licensed real estate agent but lacks
              membership to the National Association of REALTORS® (NAR). Since
              its founding in 1908, NAR has been the industry leader for the
              best real estate agents and is currently the largest trade
              association in the country. An agent's REALTOR® designation means
              they abide by NAR's stringent{' '}
              <Link
                className={styles['InfoPage__link']}
                target={'_blank'}
                href={
                  'https://www.nar.realtor/about-nar/governing-documents/code-of-ethics/2023-code-of-ethics-standards-of-practice'
                }
              >
                Code of Ethics & Standards of Practice
              </Link>
              . Adherence to this Code of Ethics requires REALTORS® to maintain
              a trustworthy business and act as upstanding members of the
              community, furthering cooperation between clients' interests. Made
              up of several Articles and Standards of Practice, this Code of
              Ethics is also reviewed regularly by a governing body to ensure
              it's keeping up with the times and any potential emerging issues.
            </p>
            <img
              src={'/static/working-with-an-agent/code-of-ethics-image.png'}
              className={styles['InfoPage__code-of-ethics-image']}
              alt="NAR Code of Ethics and Standards of Practice"
            />
            <p>
              This Code of Ethics has become a gold standard across the
              industry, and agents must elevate these practices to maintain
              their status as a REALTOR®. While there are undoubtedly some great
              agents who are not part of the organization, you know a REALTOR®
              will be putting your needs first and will provide you with an
              elevated experience.
            </p>
          </Section>
          <MessageAgentCta
            standalone
            funnel={WORKING_WITH_AN_AGENT_FUNNEL}
            fullInteractive
          />
          <Section>
            <Heading
              tag="h2"
              type="h1"
              anchor="How Can I Get Real Estate Agent and REALTOR® Recommendations?"
            >
              How Can I Get Real Estate Agent and REALTOR® Recommendations?
            </Heading>
            <p>
              The best way to get{' '}
              <Link
                className={styles['InfoPage__link']}
                target={'_blank'}
                href="/how-to-find-a-real-estate-agent"
              >
                top real estate recommendations is by using RealEstateAgents.com
              </Link>
              .
            </p>
            <div className={styles['InfoPage__callout']}>
              RealEstateAgents.com is a platform built on nearly 20 years of
              experience in analyzing agent performance and client preferences
              to create AI-powered matches that connect you with the best real
              estate agents.
            </div>
            <p>
              It can be difficult to find a real estate agent who will always
              prioritize your needs over closing the transaction. However every
              year, over 700k clients such as yourself, use the platform to find
              their ideal REALTOR® for their real estate journey; leading to
              over $2 billion in successful annual transaction volume.
            </p>
            <p>
              By analyzing past transactions, agent & client behavior and market
              dynamics we can curate a custom real estate agent list
              specifically for you.
            </p>
            <p>
              It only takes a minute to submit your request, the service is 100%
              free and our licensed customer service team is available to answer
              any questions you may have along the way. We’re committed to
              finding you the perfect agent!
            </p>
            <p>
              <Link
                className={styles['InfoPage__link']}
                target={'_blank'}
                href="/compare-agents"
              >
                Get connected with your perfect agent
              </Link>{' '}
              and begin screening real estate agents today!
            </p>
          </Section>
          <MessageAgentCta
            standalone
            funnel={WORKING_WITH_AN_AGENT_FUNNEL}
            fullInteractive
          />
        </Content>
      </SidebarContainer>
      <div className={styles['InfoPage__mobile-cta-sticky']}>
        <MessageAgentCta
          standalone
          funnel={WORKING_WITH_AN_AGENT_FUNNEL}
          fullInteractive
        />
      </div>
    </>
  );
};

export default InfoPage;
